import { useState, useEffect, useCallback, useRef } from "react";
import { CupomVenda } from "./CupomVenda";
import { calcularCupomVenda, arredondarMoeda } from "../utils/cupomVenda";
import { buscarCupomVenda } from "../utils/cupomVendaFirebase";
import { toast } from "../hooks/useToast";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { C } from "../constants/theme";
import { fbList, fbAdd, fbUpdate } from "../hooks/useFirebase";
import { useConfirm } from "../hooks/useConfirm";
import { Card } from "./ui/Card";
import { gerarPix } from "../utils/pixQR";
import { mercadoPagoEmUso } from "../utils/mercadoPago";
import { useStatusMercadoPago } from "./pedidos/PixMercadoPago";
import { PixMercadoPagoPdv } from "./pdv/PixMercadoPagoPdv";
import { getUser } from "../utils/sessao";
import { PRODUTO_DEMO_NOME } from "../constants/seedDemo";
import { listaMaquininhas, taxaMaquininha, calcularRepasse, MAX_PARCELAS } from "../constants/maquininhas";

const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Comparação por dia LOCAL (não UTC) — usar toISOString().slice(0,10) aqui
// fazia vendas feitas entre ~21h e 23h59 (horário de Brasília) caírem no dia
// seguinte em UTC e sumirem do faturamento/custo/lucro de "hoje" (ver Financeiro.jsx
// que já usa toDateString() na aba PDV — aqui ficava divergente).
const mesmoDiaLocal = (isoStr) => isoStr && new Date(isoStr).toDateString() === new Date().toDateString();

export function Caixa() {
  const [produtos, setProdutos]     = useState([]);
  const [clientes, setClientes]     = useState([]);
  const [vendas,   setVendas]       = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [salvando, setSalvando]     = useState(false);

  // cart
  const [cart,          setCart]          = useState([]);
  const [descTipo,      setDescTipo]      = useState("%");
  const [descValor,     setDescValor]     = useState("");
  const [cupom, setCupom] = useState(null);
  const salvandoRef = useRef(false);
  const [buscaProduto,  setBuscaProduto]  = useState("");

  // client / fiado
  const [clienteSel,   setClienteSel]   = useState(null);
  const [fiado,        setFiado]        = useState(false);
  const [modalCliente, setModalCliente] = useState(false);
  const [buscaCliente, setBuscaCliente] = useState("");

  // payment
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [modalPag,      setModalPag]      = useState(false);
  const [modalPix,      setModalPix]      = useState(false);
  const [pixString,     setPixString]     = useState("");
  const [qrDataUrl,     setQrDataUrl]     = useState(null);
  const [pixCopiado,    setPixCopiado]    = useState(false);
  // Pix do banco (Mercado Pago): a venda fica congelada aqui enquanto o Pix está na tela
  const [vendaPixBanco, setVendaPixBanco] = useState(null);
  const [maquininhaId,  setMaquininhaId]  = useState("");
  const [formaCartao,   setFormaCartao]   = useState("credito"); // 'debito'|'credito'
  const [parcelas,      setParcelas]      = useState(1); // 1..12, só quando credito

  // payment screen sub-step
  const [payStep,       setPayStep]       = useState(null); // null | "dinheiro"
  const [trocoRecebido, setTrocoRecebido] = useState("");
  // Com o Mercado Pago em uso, o Pix do PDV é o dele (confirma sozinho), igual aos pedidos e à loja
  const statusMP = useStatusMercadoPago(getUser()?.uid);
  const pixDoBanco = mercadoPagoEmUso(paymentConfig, statusMP);
  const pixDaChave = !!(paymentConfig?.pixEnabled && paymentConfig?.pixChave);

  // success flash
  const [sucesso, setSucesso] = useState(false);
  const [modalEstoque, setModalEstoque] = useState(false);
  const [confirmar, modalConfirm] = useConfirm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Load payment config
  useEffect(() => {
    const user = getUser();
    if (!user?.uid) return;
    getDoc(doc(db, "users", user.uid))
      .then(snap => {
        if (!snap.exists()) return;
        const cfg = snap.data()?.paymentConfig || null;
        setPaymentConfig(cfg);
        setMaquininhaId(cfg?.maquininhaPadraoId || listaMaquininhas(cfg)[0]?.id || "");
      })
      .catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [prods, cls, vs] = await Promise.all([
        fbList("produtos").catch(() => []),
        fbList("clientes").catch(() => []),
        fbList("vendas").catch(() => []),
      ]);
      setProdutos(prods);
      setClientes(cls);
      setVendas(vs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Summary ──────────────────────────────────────────────
  // vendasHojeTodas: pra listar (mostra estornada também, com selo). vendasHoje:
  // só as válidas — entram no faturamento/custo/lucro do dia (venda estornada
  // não é venda, não pode contar 2x depois que o dinheiro já voltou).
  const vendasHojeTodas = vendas.filter(v => mesmoDiaLocal(v.data)).sort((a, b) => new Date(b.data) - new Date(a.data));
  const vendasHoje  = vendasHojeTodas.filter(v => !v.estornada);
  const faturamento = vendasHoje.reduce((s, v) => s + (v.total || 0), 0);
  const custo       = vendasHoje.reduce((s, v) =>
    s + (v.itens || []).reduce((si, it) => si + (it.custo_unit || 0) * (it.qty || 1), 0), 0);
  const lucro = faturamento - custo;

  // ── Estorno de venda (devolve estoque + lança saída no caixa) ────────────
  // Só o estoque de PRODUTO volta — filamento não é mexido aqui de propósito
  // (o PDV nunca desconta/devolve filamento; a baixa de material acontece só
  // quando a peça ENTRA em estoque, na produção — ver Produtos.jsx).
  const estornarVenda = async (venda) => {
    if (venda.estornada) return;
    const nomes = (venda.itens || []).map(it => it.nome).join(", ");
    const ok = await confirmar(
      `Estornar venda de R$ ${fmt(venda.total)}${venda.cliente_nome ? ` (${venda.cliente_nome})` : ""}?\n\n` +
      `Isso devolve ${nomes || "os produtos"} ao estoque` +
      (venda.fiado ? "." : " e lança uma saída de R$ " + fmt(venda.total) + " no caixa.")
    );
    if (!ok) return;
    try {
      await Promise.all((venda.itens || []).map(it => {
        const prod = produtos.find(p => p.id === it.id);
        const novoEstoque = (parseInt(prod?.estoque) || 0) + (parseInt(it.qty) || 0);
        return fbUpdate("produtos", it.id, { estoque: novoEstoque });
      }));

      // Só lança saída se o dinheiro tinha entrado de fato (fiado nunca entrou
      // no caixa, então não tem o que devolver ali).
      if (!venda.fiado) {
        await fbAdd("caixa", {
          descricao: `Estorno venda PDV: ${nomes || "—"}`,
          valor: -Math.abs(parseFloat(venda.total) || 0), tipo: "Saída", data: new Date().toISOString(),
        });
      }

      // Mantém a venda original no histórico (auditoria) só marcada como
      // estornada, em vez de apagar — mesmo padrão do estoque de filamento.
      await fbUpdate("vendas", venda.id, { estornada: true, estornada_em: new Date().toISOString() });

      toast("Venda estornada — estoque devolvido" + (venda.fiado ? "." : " e saída lançada no caixa."), "success");
      await carregar();
    } catch (e) { toast("Erro ao estornar: " + e.message, "error"); }
  };

  // ── Valor do estoque (popup) ─────────────────────────────
  // Não é o dia — é "se eu vendesse hoje tudo que já tá pronto no estoque,
  // pelo preço atual, quanto sobraria de lucro". Mesma lógica de custo usada
  // no carrinho (custo_unit com fallback pro custo_total do cadastro).
  const produtosEmEstoque = produtos.filter(p => (p.estoque || 0) > 0);
  const faturamentoEstoque = produtosEmEstoque.reduce((s, p) => s + (p.preco || 0) * p.estoque, 0);
  const custoEstoque = produtosEmEstoque.reduce((s, p) => s + (p.custo_unit || p.custo_total || 0) * p.estoque, 0);
  const lucroEstoque = faturamentoEstoque - custoEstoque;
  const prejuizoEstoque = lucroEstoque < 0;
  const margemEstoque = faturamentoEstoque > 0 ? (lucroEstoque / faturamentoEstoque) * 100 : 0;
  const pctCustoEstoque = faturamentoEstoque > 0 ? Math.min(100, (custoEstoque / faturamentoEstoque) * 100) : 0;
  const pctLucroEstoque = 100 - pctCustoEstoque;
  const pecasEmEstoque = produtosEmEstoque.reduce((s, p) => s + p.estoque, 0);

  // ── Cart math ────────────────────────────────────────────
  const subtotal = cart.reduce((s, it) => s + it.preco * it.qty, 0);
  let descontoCupom=0;
  // O preview não muda silenciosamente enquanto um PIX está na tela.
  // A validade atual é conferida ao gerar o PIX e novamente ao registrar.
  if(cupom) {try{descontoCupom=calcularCupomVenda(cupom,subtotal,0).desconto;}catch{}}
  const descontoManual = descValor ? descTipo === "%" ? subtotal * (parseFloat(descValor) / 100) : parseFloat(descValor) : 0;
  const desconto = arredondarMoeda(Math.min(subtotal,Math.max(0,cupom ? descontoCupom : descontoManual || 0)));
  const total = arredondarMoeda(Math.max(0, subtotal - desconto));

  const conferirCupom = async () => {
    if(!cupom) return null;
    const atual=await buscarCupomVenda(cupom.codigo,subtotal);
    const cotacao=calcularCupomVenda(atual,subtotal);
    if(cotacao.total !== total) throw new Error("O cupom mudou. Remova e aplique novamente para conferir o valor.");
    return cotacao.cupom;
  };

  // ── Cart actions ─────────────────────────────────────────
  const addAoCart = (prod) => {
    setCart(c => {
      const ex = c.find(x => x.id === prod.id);
      if (ex) return c.map(x => x.id === prod.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { id: prod.id, nome: prod.nome, preco: prod.preco || 0, custo_unit: prod.custo_unit || prod.custo_total || 0, qty: 1, estoque: prod.estoque || 0 }];
    });
  };

  const setQty = (id, qty) => {
    if (qty <= 0) { setCart(c => c.filter(x => x.id !== id)); return; }
    setCart(c => c.map(x => x.id === id ? { ...x, qty } : x));
  };

  // ── Finalizar (salva no Firestore) ───────────────────────
  const salvarVenda = async (formaPagamento) => {
    if (cart.length === 0 || salvandoRef.current) return;
    salvandoRef.current=true;
    setSalvando(true);
    try {
      const cupomConfirmado=await conferirCupom();
      const agora = new Date().toISOString();
      const itens = cart.map(it => ({
        id: it.id, nome: it.nome, preco: it.preco,
        custo_unit: it.custo_unit, qty: it.qty,
        subtotal: it.preco * it.qty,
      }));

      await fbAdd("vendas", {
        data: agora, itens, subtotal, desconto, total,
        cupom:cupomConfirmado,
        cliente_id:   clienteSel?.id   || "",
        cliente_nome: clienteSel?.nome || "",
        fiado, formaPagamento,
      });

      await Promise.all(cart.map(it => {
        const prod = produtos.find(p => p.id === it.id);
        const novoEstoque = Math.max(0, (prod?.estoque || 0) - it.qty);
        return fbUpdate("produtos", it.id, { estoque: novoEstoque });
      }));

      if (!fiado) {
        const nomes = itens.map(it => it.nome).join(", ");
        const labelPag = { dinheiro:"Dinheiro", pix:"PIX", cartao:"Cartão" }[formaPagamento] || formaPagamento;
        await fbAdd("caixa", {
          descricao: `Venda PDV (${labelPag}): ${nomes}`,
          valor: total, tipo: "Entrada", data: agora, formaPagamento,
          cupom:cupomConfirmado, desconto,
        });
      }

      if (clienteSel?.id) {
        await fbUpdate("clientes", clienteSel.id, { ultimoPedido: agora });
      }

      setCart([]); setDescValor(""); setCupom(null); setClienteSel(null); setFiado(false);
      setSucesso(true);
      setTimeout(() => setSucesso(false), 2500);
      await carregar();
    } catch(error) {
      toast(error?.code ? "Não foi possível registrar a venda. Confira a conexão e tente novamente." : error.message,"error");
    } finally {
      salvandoRef.current=false;
      setSalvando(false);
    }
  };

  // ── Abrir seleção de pagamento ───────────────────────────
  const abrirPagamento = () => {
    if (cart.length === 0) return;
    setModalPag(true);
  };

  // ── PIX ─────────────────────────────────────────────────
  const iniciarPix = async () => {
    let cupomConfirmado;
    try { cupomConfirmado = await conferirCupom(); } catch(error) { toast(error?.code ? "Não foi possível validar o cupom agora." : error.message,"error"); return; }
    setModalPag(false); setPayStep(null); setTrocoRecebido("");
    if (pixDoBanco) {
      // O servidor confere o total e registra a venda quando o Mercado Pago confirmar
      setVendaPixBanco({
        itens: cart.map(it => ({ id: it.id, nome: it.nome, preco: it.preco, custo_unit: it.custo_unit, qty: it.qty })),
        subtotal, desconto, total, cupom: cupomConfirmado || null,
        cliente_id: clienteSel?.id || "", cliente_nome: clienteSel?.nome || "",
      });
      return;
    }
    const cfg = paymentConfig;
    if (!cfg?.pixEnabled || !cfg?.pixChave) {
      toast("Configure o PIX em Configurações → Pagamentos antes de usar.", "warning");
      return;
    }
    const payload = gerarPix({
      chave: cfg.pixChave,
      beneficiario: cfg.pixBeneficiario || "BENEFICIARIO",
      cidade: cfg.pixCidade || "CIDADE",
      valor: total,
    });
    setPixString(payload);
    setQrDataUrl(null);
    setPixCopiado(false);
    setModalPix(true);

    // Gera QR code via import dinâmico
    try {
      const QRCode = await import("qrcode");
      const url = await QRCode.toDataURL(payload, { width: 240, margin: 2, color: { dark: "#000", light: "#fff" } });
      setQrDataUrl(url);
    } catch {}
  };

  const copiarPix = () => {
    navigator.clipboard.writeText(pixString).then(() => {
      setPixCopiado(true);
      setTimeout(() => setPixCopiado(false), 2500);
    });
  };

  const cancelarPix = () => {
    setModalPix(false);
  };

  // Pix do banco confirmado: a venda, o estoque e o caixa já foram registrados pelo servidor
  const concluirPixBanco = useCallback(() => {
    setVendaPixBanco(null);
    setCart([]); setDescValor(""); setCupom(null); setClienteSel(null); setFiado(false);
    setSucesso(true);
    setTimeout(() => setSucesso(false), 2500);
    carregar();
  }, [carregar]);

  const confirmarPix = async () => {
    if (!await confirmar(`Confirmar recebimento de R$ ${fmt(total)} via PIX?`)) return;
    setModalPix(false);
    await salvarVenda("pix");
  };

  // ── Maquininha: calculadora de taxa (débito/crédito à vista) ─────
  const maquininhas = listaMaquininhas(paymentConfig);
  const maquininhaAtual = maquininhas.find(m => m.id === maquininhaId) || maquininhas[0];
  const taxaPct = taxaMaquininha(maquininhaAtual, formaCartao === "debito" ? "debito" : parcelas);
  const { acrescimo, valorCobrar } = calcularRepasse(total, taxaPct);

  // ── WhatsApp receipt ──────────────────────────────────────
  const gerarWA = () => {
    if (!clienteSel?.celular) return;
    const linhas = cart.map(it => `  • ${it.nome} x${it.qty} = R$ ${fmt(it.preco * it.qty)}`).join("\n");
    let msg = `Olá ${clienteSel.nome}! 😊\n\nSegue o resumo da sua compra:\n\n${linhas}`;
    if (desconto > 0) msg += `\n\n  Desconto${cupom ? ` (${cupom.codigo})` : ""}: -R$ ${fmt(desconto)}`;
    msg += `\n\n*Total: R$ ${fmt(total)}*`;
    if (fiado) msg += "\n\n_(lançado como fiado)_";
    msg += "\n\nObrigado pela preferência! 🙏";
    const numero = clienteSel.celular.replace(/\D/g, "");
    window.open(`https://wa.me/55${numero}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // ── Filtered clients ──────────────────────────────────────
  const clientesFiltrados = clientes.filter(c =>
    !buscaCliente || c.nome?.toLowerCase().includes(buscaCliente.toLowerCase())
  );

  if (loading) return <div style={{ color: C.muted, textAlign: "center", padding: 40 }}>⏳ Carregando...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {modalConfirm}

      {/* ── Tela cheia de pagamento ───────────────────────── */}
      {modalPag && (
        <div data-tour="caixa-modal-pagamento" style={{ position:"fixed", inset:0, background:"var(--pc-ui-04111f, #04111f)", zIndex:600, display:"flex", flexDirection:"column" }}>

          {/* Header */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:"1px solid var(--pc-white-6, rgba(255,255,255,0.06))", flexShrink:0 }}>
            <div style={{ fontSize:12, color:C.muted, fontWeight:700, letterSpacing:1 }}>
              {payStep === "dinheiro" ? "💵 PAGAMENTO EM DINHEIRO" : payStep === "cartao" ? "💳 PAGAMENTO NO CARTÃO" : "💳 FORMA DE PAGAMENTO"}
            </div>
            <button data-tour="caixa-fechar-pagamento" onClick={() => { setModalPag(false); setPayStep(null); setTrocoRecebido(""); }}
              style={{ background:"none", border:"none", color:C.muted, fontSize:22, cursor:"pointer", lineHeight:1, padding:4 }}>✕</button>
          </div>

          {/* Total destaque */}
          <div style={{ textAlign:"center", padding:"28px 20px 20px", flexShrink:0 }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:700, letterSpacing:2, marginBottom:10 }}>TOTAL A PAGAR</div>
            <div style={{ fontSize:isMobile?52:64, fontWeight:900, color:C.accent2, lineHeight:1, letterSpacing:-1 }}>
              R$ {fmt(total)}
            </div>
            {desconto > 0 && (
              <div style={{ fontSize:12, color:C.muted, marginTop:8 }}>
                {cupom ? `Cupom ${cupom.codigo}` : "Desconto"}: <span style={{ color:C.danger }}>-R$ {fmt(desconto)}</span>
              </div>
            )}
            {clienteSel && (
              <div style={{ fontSize:12, color:C.muted, marginTop:6 }}>
                👤 {clienteSel.nome}
              </div>
            )}
          </div>

          {/* Conteúdo rolável */}
          <div style={{ flex:1, overflowY:"auto", padding:"0 20px 32px" }}>

            {/* Etapa 0: escolher método */}
            {payStep === null && (
              <div style={{ display:"flex", flexDirection:"column", gap:14, maxWidth:480, margin:"0 auto" }}>

                <button data-tour="caixa-opcao-dinheiro" onClick={() => setPayStep("dinheiro")}
                  style={{ display:"flex", alignItems:"center", gap:18, padding:"22px 24px", borderRadius:18, background:"rgba(0,212,170,0.07)", border:"2px solid rgba(0,212,170,0.3)", cursor:"pointer", textAlign:"left", transition:"all .15s" }}>
                  <span style={{ fontSize:38 }}>💵</span>
                  <div>
                    <div style={{ fontWeight:900, fontSize:18, color:C.accent2 }}>Dinheiro</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>Calcular troco automaticamente</div>
                  </div>
                </button>

                {(pixDoBanco || pixDaChave) && (
                  <button onClick={iniciarPix}
                    style={{ display:"flex", alignItems:"center", gap:18, padding:"22px 24px", borderRadius:18, background:"rgba(37,211,102,0.07)", border:"2px solid rgba(37,211,102,0.3)", cursor:"pointer", textAlign:"left", transition:"all .15s" }}>
                    <span style={{ fontSize:38 }}>📱</span>
                    <div>
                      <div style={{ fontWeight:900, fontSize:18, color:"#25d366" }}>{pixDoBanco ? "PIX Mercado Pago" : "PIX"}</div>
                      <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{pixDoBanco ? "Confirma sozinho quando o cliente pagar" : "QR Code com valor já preenchido"}</div>
                    </div>
                  </button>
                )}

                <button onClick={() => setPayStep("cartao")}
                  style={{ display:"flex", alignItems:"center", gap:18, padding:"22px 24px", borderRadius:18, background:"rgba(0,158,227,0.07)", border:"2px solid rgba(0,158,227,0.3)", cursor:"pointer", textAlign:"left", transition:"all .15s" }}>
                  <span style={{ fontSize:38 }}>💳</span>
                  <div>
                    <div style={{ fontWeight:900, fontSize:18, color:"#009ee3" }}>Cartão</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>Débito ou crédito na sua maquininha</div>
                  </div>
                </button>
              </div>
            )}

            {/* Etapa cartão: confirmação manual + calculadora de taxa */}
            {payStep === "cartao" && (
              <div style={{ display:"flex", flexDirection:"column", gap:16, maxWidth:480, margin:"0 auto" }}>

                {/* Instrução central */}
                <div style={{ textAlign:"center", padding:"20px 16px 4px" }}>
                  <div style={{ fontSize:48, marginBottom:10 }}>💳</div>
                  <div style={{ fontSize:16, fontWeight:800, color:C.text, marginBottom:8 }}>Passe o cartão na maquininha</div>
                  <div style={{ fontSize:13, color:C.muted, lineHeight:1.6 }}>
                    Após a aprovação na maquininha,<br />toque em <strong style={{ color:"#009ee3" }}>Confirmar</strong> para registrar a venda.
                  </div>
                </div>

                {/* Calculadora de taxa da maquininha — repassa a taxa pro cliente */}
                {maquininhas.length > 0 && (
                  <div style={{ background:"rgba(0,158,227,0.05)", border:"1px solid rgba(0,158,227,0.2)", borderRadius:16, padding:"16px 18px" }}>
                    <div style={{ fontSize:11, color:C.muted, fontWeight:700, letterSpacing:1, marginBottom:10 }}>QUANTO COBRAR NA MAQUININHA (REPASSANDO A TAXA)</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                      <select value={maquininhaId} onChange={e => setMaquininhaId(e.target.value)}
                        style={{ flex:"1 1 140px", padding:"9px 10px", background:C.bg, border:"1px solid "+C.border, borderRadius:10, color:C.text, fontSize:13, outline:"none" }}>
                        {maquininhas.map(m => <option key={m.id} value={m.id}>{m.nome || "Maquininha"}</option>)}
                      </select>
                      <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                        {[["debito","Débito"],["credito","Crédito"]].map(([v,l]) => (
                          <button key={v} onClick={() => setFormaCartao(v)}
                            style={{ padding:"9px 12px", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer",
                              background: formaCartao===v ? "rgba(0,158,227,0.15)" : "transparent",
                              border:"1px solid "+(formaCartao===v ? "#009ee3" : C.border),
                              color: formaCartao===v ? "#009ee3" : C.muted }}>
                            {l}
                          </button>
                        ))}
                      </div>
                      {formaCartao === "credito" && (
                        <select value={parcelas} onChange={e => setParcelas(Number(e.target.value))}
                          style={{ flexShrink:0, padding:"9px 10px", background:C.bg, border:"1px solid "+C.border, borderRadius:10, color:C.text, fontSize:13, outline:"none" }}>
                          {Array.from({ length: MAX_PARCELAS }, (_, i) => i+1).map(n => (
                            <option key={n} value={n}>{n}x</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:C.muted, marginBottom:4 }}>
                      <span>Valor da venda</span><span>R$ {fmt(total)}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:C.muted, marginBottom:8 }}>
                      <span>+ Taxa ({fmt(taxaPct)}%{formaCartao==="credito" ? ` · ${parcelas}x` : ""}) repassada ao cliente</span><span style={{ color:C.warning }}>+R$ {fmt(acrescimo)}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:14, fontWeight:900, color:C.text, borderTop:"1px solid "+C.border, paddingTop:8 }}>
                      <span>Cobrar do cliente na maquininha</span><span style={{ color:C.accent2 }}>R$ {fmt(valorCobrar)}</span>
                    </div>
                    <div style={{ fontSize:10, color:C.muted, marginTop:8, lineHeight:1.5 }}>
                      Digite esse valor na maquininha em vez de R$ {fmt(total)} — assim a taxa sai do cliente, não do seu bolso. A venda continua registrada por R$ {fmt(total)}.
                    </div>
                  </div>
                )}

                <button
                  onClick={async () => { if (!await confirmar(`Confirmar pagamento de R$ ${fmt(total)} no cartão?`)) return; setModalPag(false); setPayStep(null); await salvarVenda("cartao"); }}
                  disabled={salvando}
                  style={{ width:"100%", padding:"18px 0", background:"linear-gradient(135deg,#009ee3,var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:16, color:"#fff", fontSize:16, fontWeight:900, cursor:"pointer", letterSpacing:0.5 }}>
                  {salvando ? "⏳ Salvando..." : "✅ Cartão aprovado — confirmar"}
                </button>

                <button onClick={() => setPayStep(null)}
                  style={{ background:"none", border:"none", color:C.muted, fontSize:14, cursor:"pointer", padding:"6px 0", textAlign:"center" }}>
                  ← Voltar
                </button>
              </div>
            )}

            {/* Etapa dinheiro: calculadora de troco */}
            {payStep === "dinheiro" && (
              <div data-tour="caixa-tela-troco" style={{ display:"flex", flexDirection:"column", gap:16, maxWidth:480, margin:"0 auto" }}>

                <div>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:700, letterSpacing:1, marginBottom:8 }}>VALOR RECEBIDO</div>
                  <input
                    type="number"
                    value={trocoRecebido}
                    onChange={e => setTrocoRecebido(e.target.value)}
                    placeholder="0,00"
                    autoFocus
                    style={{ width:"100%", boxSizing:"border-box", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"2px solid rgba(0,212,170,0.35)", borderRadius:14, color:C.text, fontSize:isMobile?36:44, fontWeight:900, padding:"14px 20px", outline:"none", textAlign:"center" }}
                  />
                </div>

                {/* Botões de valor rápido */}
                <div style={{ display:"flex", gap:8 }}>
                  {[5,10,20,50,100].map(v => (
                    <button key={v} onClick={() => setTrocoRecebido(String(v))}
                      style={{ flex:1, padding:"11px 4px", background:"var(--pc-white-4, rgba(255,255,255,0.04))", border:"1px solid "+C.border, borderRadius:10, color:C.text, fontSize:14, fontWeight:700, cursor:"pointer" }}>
                      {v}
                    </button>
                  ))}
                </div>

                {/* Exibição do troco */}
                <div style={{ background:"var(--pc-white-3, rgba(255,255,255,0.03))", border:"1px solid rgba(0,212,170,0.15)", borderRadius:14, padding:"18px 20px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:700, letterSpacing:1, marginBottom:8 }}>TROCO</div>
                  {trocoRecebido ? (
                    <>
                      <div style={{ fontSize:44, fontWeight:900, lineHeight:1, color:(parseFloat(trocoRecebido)||0) >= total ? C.accent2 : C.danger }}>
                        R$ {fmt(Math.max(0, (parseFloat(trocoRecebido)||0) - total))}
                      </div>
                      {(parseFloat(trocoRecebido)||0) < total && (
                        <div style={{ fontSize:12, color:C.danger, marginTop:8 }}>
                          Faltam R$ {fmt(total - (parseFloat(trocoRecebido)||0))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize:22, fontWeight:900, color:C.muted }}>R$ —</div>
                  )}
                </div>

                <button
                  onClick={async () => { if (!await confirmar(`Confirmar pagamento de R$ ${fmt(total)} em dinheiro?`)) return; setModalPag(false); setPayStep(null); setTrocoRecebido(""); await salvarVenda("dinheiro"); }}
                  disabled={salvando || (!!trocoRecebido && (parseFloat(trocoRecebido)||0) < total)}
                  style={{ width:"100%", padding:"18px 0", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:16, color:"var(--pc-ui-04111f, #04111f)", fontSize:16, fontWeight:900, cursor:"pointer", letterSpacing:0.5, opacity:(!!trocoRecebido && (parseFloat(trocoRecebido)||0) < total) ? 0.4 : 1, transition:"opacity .2s" }}>
                  {salvando ? "⏳ Salvando..." : "✅ Confirmar pagamento"}
                </button>

                <button onClick={() => { setPayStep(null); setTrocoRecebido(""); }}
                  style={{ background:"none", border:"none", color:C.muted, fontSize:14, cursor:"pointer", padding:"6px 0", textAlign:"center" }}>
                  ← Voltar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: PIX QR Code ───────────────────────────── */}
      {vendaPixBanco && (
        <PixMercadoPagoPdv venda={vendaPixBanco} emailInicial={clienteSel?.email || ""} onPago={concluirPixBanco} onFechar={() => setVendaPixBanco(null)} />
      )}

      {modalPix && (
        <>
          <div onClick={() => setModalPix(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:500 }} />
          <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"min(400px,95vw)", background:C.panel, border:"1px solid rgba(37,211,102,0.3)", borderRadius:20, padding:28, zIndex:501, boxShadow:"0 24px 64px rgba(0,0,0,0.7)" }}>
            <div style={{ fontWeight:900, fontSize:15, color:"#25d366", marginBottom:4, letterSpacing:1 }}>📱 PAGUE COM PIX</div>
            <div style={{ color:C.muted, fontSize:12, marginBottom:20 }}>Valor: <strong style={{ color:C.accent2, fontSize:16 }}>R$ {fmt(total)}</strong></div>

            {/* QR Code */}
            <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR Code PIX" style={{ width:200, height:200, borderRadius:12, border:"3px solid #25d366" }} />
              ) : (
                <div style={{ width:200, height:200, borderRadius:12, background:C.card, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:C.muted }}>
                  ⏳ Gerando...
                </div>
              )}
            </div>

            {/* Código copia e cola */}
            <div style={{ fontSize:10, color:C.muted, fontWeight:700, letterSpacing:1, marginBottom:6 }}>CÓDIGO PIX COPIA E COLA</div>
            <div style={{ background:C.bg, border:"1px solid "+C.border, borderRadius:8, padding:"10px 12px", fontSize:10, color:C.muted, wordBreak:"break-all", marginBottom:12, lineHeight:1.5, fontFamily:"monospace" }}>
              {pixString.slice(0, 80)}...
            </div>
            <button onClick={copiarPix}
              style={{ width:"100%", padding:"10px 0", marginBottom:10, background:pixCopiado?"rgba(37,211,102,0.15)":"rgba(0,212,170,0.1)", border:"1px solid "+(pixCopiado?"rgba(37,211,102,0.4)":"rgba(0,212,170,0.25)"), borderRadius:10, color:pixCopiado?"#25d366":C.accent2, fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>
              {pixCopiado ? "✓ Código copiado!" : "📋 Copiar código PIX"}
            </button>

            <div style={{ fontSize:11, color:C.muted, textAlign:"center", marginBottom:16, lineHeight:1.5 }}>
              Escaneie o QR Code ou copie o código no app do banco.
              Após confirmar o recebimento, clique no botão abaixo.
            </div>


            <div style={{ display:"flex", gap:8 }}>
              <button onClick={confirmarPix}
                style={{ flex:1, padding:"12px 0", background:"linear-gradient(135deg,#25d366,#128c7e)", border:"none", borderRadius:12, color:"#fff", fontSize:13, fontWeight:900, cursor:"pointer" }}>
                ✅ Confirmar recebimento
              </button>
              <button onClick={cancelarPix}
                style={{ padding:"12px 16px", background:"none", border:"1px solid "+C.border, borderRadius:12, color:C.muted, fontSize:13, cursor:"pointer" }}>
                Cancelar
              </button>
            </div>
          </div>
        </>
      )}

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 10 }}>
        {[
          { label: "Vendas hoje", value: vendasHoje.length, prefix: "", suffix: "" },
          { label: "Faturamento", value: fmt(faturamento), prefix: "R$ ", suffix: "" },
          { label: "Custo", value: fmt(custo), prefix: "R$ ", suffix: "" },
          { label: "Lucro", value: fmt(lucro), prefix: "R$ ", suffix: "", color: lucro >= 0 ? C.success : C.danger },
        ].map(({ label, value, prefix, color }) => (
          <Card key={label} style={{ padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, letterSpacing: 1, marginBottom: 6, textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: color || C.text }}>{prefix}{value}</div>
          </Card>
        ))}
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr)" : "minmax(0,1fr) 360px", gap: 16, alignItems: "start" }}>

        {/* Product grid */}
        <Card data-tour="caixa-produtos" style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            {/* No celular o título some: o botão de valor fica à esquerda e a pesquisa
                ocupa o resto da linha, sem estourar a largura do card. */}
            {!isMobile && <div style={{ fontWeight: 800, fontSize: 14, color: C.text, letterSpacing: 1, flex: 1 }}>PRODUTOS</div>}
            <button onClick={() => setModalEstoque(true)}
              style={{ background: "rgba(0,212,170,0.1)", border: "1px solid rgba(0,212,170,0.35)", borderRadius: 8, color: C.accent2, fontSize: 12, fontWeight: 800, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
              💰 Valor do estoque
            </button>
            <div style={{ position: "relative", ...(isMobile ? { flex: 1, minWidth: 0 } : { flexShrink: 0 }) }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 13, pointerEvents: "none" }}>🔍</span>
              <input
                value={buscaProduto}
                onChange={e => setBuscaProduto(e.target.value)}
                placeholder="Pesquisar..."
                style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12, padding: "6px 10px 6px 28px", outline: "none", boxSizing: "border-box", width: isMobile ? "100%" : 150 }}
              />
              {buscaProduto && (
                <button onClick={() => setBuscaProduto("")} style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0 }}>✕</button>
              )}
            </div>
          </div>
          {produtos.length === 0 ? (
            <div style={{ color: C.muted, textAlign: "center", padding: 32 }}>Nenhum produto cadastrado.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
              {produtos.filter(p => (p.estoque || 0) > 0 && (!buscaProduto || p.nome?.toLowerCase().includes(buscaProduto.toLowerCase()))).map(prod => {
                const noCart = cart.find(x => x.id === prod.id);
                const semEstoque = (prod.estoque || 0) <= 0;
                return (
                  <button key={prod.id} data-tour={prod.nome === PRODUTO_DEMO_NOME ? "caixa-produto-demo" : undefined} onClick={() => addAoCart(prod)}
                    style={{
                      background: noCart ? "rgba(0,212,170,0.08)" : C.bg,
                      border: `1px solid ${noCart ? "rgba(0,212,170,0.4)" : C.border}`,
                      borderRadius: 12, padding: 10, cursor: "pointer", opacity: 1,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                      transition: "all .15s", textAlign: "center",
                    }}>
                    {prod.fotoBase64 ? (
                      <img src={prod.fotoBase64} alt={prod.nome} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }} />
                    ) : (
                      <div style={{ width: 64, height: 64, borderRadius: 8, background: C.panel, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📦</div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{prod.nome}</div>
                    <div style={{ fontSize: 12, color: C.accent2, fontWeight: 800 }}>R$ {fmt(prod.preco)}</div>
                    <div style={{ fontSize: 10, color: semEstoque ? C.danger : C.muted }}>
                      {semEstoque ? "Sem estoque" : `Estoque: ${prod.estoque}`}
                    </div>
                    {noCart && (
                      <div style={{ background: "var(--pc-ui-00d4aa, #00d4aa)", color: "var(--pc-ui-04111f, #04111f)", borderRadius: 6, fontSize: 10, fontWeight: 900, padding: "2px 8px" }}>
                        {noCart.qty} no cart
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Cart panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card data-tour="caixa-carrinho" style={{ padding: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 12, letterSpacing: 1 }}>CARRINHO</div>

            {cart.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>Clique nos produtos para adicionar</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                {cart.map(it => (
                  <div key={it.id} data-tour={it.nome === PRODUTO_DEMO_NOME ? "caixa-carrinho-item-demo" : undefined} style={{ display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 10, padding: "8px 10px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.nome}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>R$ {fmt(it.preco)} × {it.qty}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                      <button onClick={() => setQty(it.id, it.qty - 1)} style={btnSmall}>−</button>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.text, minWidth: 18, textAlign: "center" }}>{it.qty}</span>
                      <button onClick={() => setQty(it.id, it.qty + 1)} style={btnSmall}>+</button>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.accent2, minWidth: 60, textAlign: "right" }}>R$ {fmt(it.preco * it.qty)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Totals */}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.muted }}>
                <span>Subtotal</span><span>R$ {fmt(subtotal)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ display:"flex", gap:3, flexShrink:0 }}>
                  {["%","R$"].map(t=>(
                    <button key={t} disabled={!!cupom} onClick={()=>setDescTipo(t)}
                      style={{ background:descTipo===t?"rgba(0,212,170,0.15)":C.panel, border:`1px solid ${descTipo===t?C.accent2:C.border}`, borderRadius:6, color:descTipo===t?C.accent2:C.muted, fontSize:12, fontWeight:800, padding:"4px 8px", cursor:"pointer" }}>
                      {t}
                    </button>
                  ))}
                </div>
                <input disabled={!!cupom} value={descValor} onChange={e => setDescValor(e.target.value)} placeholder={cupom ? "Cupom aplicado" : "Desconto manual"} type="number" min="0"
                  style={{ flex: 1, minWidth:0, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, padding: "4px 8px", outline: "none" }} />
                {desconto > 0 && <span style={{ fontSize: 11, color: C.danger, flexShrink: 0 }}>-R$ {fmt(desconto)}</span>}
              </div>
              <CupomVenda subtotal={subtotal} cupom={cupom} disabled={salvando} onChange={value=>{setCupom(value);if(value)setDescValor("");}} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 900, color: C.text, marginTop: 4 }}>
                <span>Total</span>
                <span style={{ color: C.accent2 }}>R$ {fmt(total)}</span>
              </div>
            </div>
          </Card>

          {/* Client / fiado */}
          <Card style={{ padding: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: C.muted, letterSpacing: 1, marginBottom: 10 }}>CLIENTE (opcional)</div>
            {clienteSel ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: C.bg, borderRadius: 10, padding: "8px 12px", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{clienteSel.nome}</div>
                  {clienteSel.celular && <div style={{ fontSize: 11, color: C.muted }}>{clienteSel.celular}</div>}
                </div>
                <button onClick={() => { setClienteSel(null); setFiado(false); }} style={{ background: "none", border: "none", color: C.danger, fontSize: 16, cursor: "pointer" }}>✕</button>
              </div>
            ) : (
              <button onClick={() => setModalCliente(true)}
                style={{ width: "100%", background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 10, color: C.muted, fontSize: 13, padding: "10px 0", cursor: "pointer", marginBottom: 10 }}>
                + Vincular cliente
              </button>
            )}
            {clienteSel && (
              <button onClick={() => setFiado(f => !f)}
                style={{ width: "100%", background: fiado ? "rgba(239,68,68,0.1)" : C.bg, border: `1px solid ${fiado ? "rgba(239,68,68,0.5)" : C.border}`, borderRadius: 10, color: fiado ? C.danger : C.muted, fontSize: 13, fontWeight: fiado ? 800 : 400, padding: "8px 0", cursor: "pointer", transition: "all .2s" }}>
                {fiado ? "🔴 Fiado (não entra no caixa)" : "Lançar como fiado?"}
              </button>
            )}
          </Card>

          {/* Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {clienteSel?.celular && cart.length > 0 && (
              <button onClick={gerarWA}
                style={{ width: "100%", background: "linear-gradient(135deg,#25d366,#128c7e)", border: "none", borderRadius: 12, color: "#fff", fontSize: 14, fontWeight: 800, padding: "12px 0", cursor: "pointer", letterSpacing: 0.5 }}>
                💬 Enviar comprovante
              </button>
            )}
            <button data-tour="caixa-pagar" onClick={abrirPagamento}
              disabled={cart.length === 0 || salvando}
              style={{ width: "100%", background: cart.length === 0 ? C.panel : "linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border: "none", borderRadius: 12, color: cart.length === 0 ? C.muted : "var(--pc-ui-04111f, #04111f)", fontSize: 14, fontWeight: 900, padding: "14px 0", cursor: cart.length === 0 ? "not-allowed" : "pointer", letterSpacing: 1, transition: "all .2s" }}>
              {salvando ? "⏳ Salvando..." : sucesso ? "✅ Venda registrada!" : `💳 Pagar  R$ ${fmt(total)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Vendas de hoje — só aqui dá pra estornar (devolver estoque + lançar
          saída no caixa). Histórico completo/outros dias fica em Financeiro
          → PDV, mas o estorno em si mora no PDV mesmo, perto de onde a venda
          foi feita. */}
      {vendasHojeTodas.length > 0 && (
        <Card style={{ padding: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: C.text, letterSpacing: 1, marginBottom: 12 }}>VENDAS DE HOJE</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {vendasHojeTodas.map(v => {
              const hora = new Date(v.data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
              const pagIcon = { dinheiro: "💵", pix: "📱", cartao: "💳" }[v.formaPagamento] || "💳";
              return (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, background: C.bg, border: "1px solid " + C.border, borderRadius: 10, padding: "9px 12px", opacity: v.estornada ? 0.55 : 1 }}>
                  <span style={{ fontSize: 11, color: C.muted, fontWeight: 600, flexShrink: 0 }}>{hora}</span>
                  <span style={{ fontSize: 12 }}>{pagIcon}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: v.estornada ? "line-through" : "none" }}>
                    {(v.itens || []).map(it => `${it.nome} ×${it.qty}`).join(", ")}
                    {v.cupom?.codigo && <small style={{display:"block",color:C.accent2,fontSize:10}}>Cupom {v.cupom.codigo} · − R$ {fmt(v.desconto)}</small>}
                  </span>
                  {v.cliente_nome && <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{v.cliente_nome}</span>}
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.success, flexShrink: 0 }}>R$ {fmt(v.total)}</span>
                  {v.estornada ? (
                    <span style={{ fontSize: 10, fontWeight: 800, color: C.danger, background: "rgba(239,68,68,0.1)", borderRadius: 6, padding: "3px 8px", flexShrink: 0 }}>ESTORNADA</span>
                  ) : (
                    <button onClick={() => estornarVenda(v)} title="Estornar / reembolsar"
                      style={{ background: "none", border: "1px solid " + C.border, borderRadius: 8, color: C.danger, fontSize: 11, fontWeight: 700, padding: "5px 10px", cursor: "pointer", flexShrink: 0 }}>
                      ↩️ Estornar
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Client modal */}
      {modalCliente && (
        <>
          <div onClick={() => { setModalCliente(false); setBuscaCliente(""); }}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(420px,95vw)", background: C.panel, borderRadius: 20, border: `1px solid ${C.border}`, zIndex: 201, padding: 24, maxHeight: "80vh", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 16, color: C.text }}>Selecionar cliente</div>
            <input value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)} placeholder="Buscar por nome..." autoFocus
              style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontSize: 14, padding: "10px 14px", outline: "none" }} />
            <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              {clientesFiltrados.length === 0 ? (
                <div style={{ color: C.muted, textAlign: "center", padding: 24 }}>Nenhum cliente encontrado</div>
              ) : clientesFiltrados.map(c => (
                <button key={c.id} onClick={() => { setClienteSel(c); setModalCliente(false); setBuscaCliente(""); }}
                  style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{c.nome}</div>
                    {c.celular && <div style={{ fontSize: 11, color: C.muted }}>{c.celular}</div>}
                  </div>
                  {c.tipo && (
                    <span style={{ fontSize: 10, fontWeight: 800, color: "#fff", background: tipoCorMap[c.tipo] || C.muted, borderRadius: 6, padding: "2px 7px" }}>{c.tipo}</span>
                  )}
                </button>
              ))}
            </div>
            <button onClick={() => { setModalCliente(false); setBuscaCliente(""); }}
              style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted, fontSize: 13, padding: "8px 0", cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </>
      )}

      {/* Modal: valor do estoque — quanto dá se vender tudo que já tá pronto,
          pelo preço de hoje. Aberto só por esse botão, some ao clicar fora/✕. */}
      {modalEstoque && (
        <>
          <div onClick={() => setModalEstoque(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200 }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: "min(420px,95vw)", background: C.panel, border: `1px solid ${C.border}`, borderRadius: 20, boxShadow: "0 24px 64px rgba(0,0,0,0.7)", zIndex: 201, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "22px 24px 16px" }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 900, color: C.text }}>💰 Valor do estoque</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Se tudo que está no estoque hoje for vendido pelo preço atual</div>
              </div>
              <button onClick={() => setModalEstoque(false)}
                style={{ width: 30, height: 30, borderRadius: 8, background: C.bg, border: "none", color: C.muted, fontSize: 15, cursor: "pointer", flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 24px 26px" }}>
              <div style={{ width: 164, height: 164, position: "relative", marginBottom: 18 }}>
                <svg viewBox="0 0 120 120" width="164" height="164">
                  <circle cx="60" cy="60" r="48" fill="none" stroke="var(--pc-ui-30394b, #30394b)" strokeWidth="13" />
                  {faturamentoEstoque > 0 && <>
                    <circle cx="60" cy="60" r="48" fill="none" stroke="var(--pc-ui-38bdf8, #38bdf8)" strokeWidth="13" pathLength="100" strokeDasharray={`${pctCustoEstoque} ${100 - pctCustoEstoque}`} strokeDashoffset="0" transform="rotate(-90 60 60)" />
                    <circle cx="60" cy="60" r="48" fill="none" stroke="var(--pc-ui-00d4aa, #00d4aa)" strokeWidth="13" pathLength="100" strokeDasharray={`${pctLucroEstoque} ${100 - pctLucroEstoque}`} strokeDashoffset={-pctCustoEstoque} transform="rotate(-90 60 60)" />
                  </>}
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3 }}>
                  <strong style={{ fontSize: 32, color: prejuizoEstoque ? C.danger : C.text }}>{faturamentoEstoque > 0 ? `${margemEstoque.toFixed(0)}%` : "—"}</strong>
                  <span style={{ fontSize: 12, color: C.muted }}>{prejuizoEstoque ? "Prejuízo" : "Margem"}</span>
                </div>
              </div>
              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 11 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: C.muted }}><i style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--pc-ui-38bdf8, #38bdf8)", display: "inline-block" }} />Custo do estoque</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>R$ {fmt(custoEstoque)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: C.muted }}><i style={{ width: 8, height: 8, borderRadius: "50%", background: prejuizoEstoque ? C.danger : "var(--pc-ui-00d4aa, #00d4aa)", display: "inline-block" }} />{prejuizoEstoque ? "Prejuízo" : "Lucro potencial"}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: prejuizoEstoque ? C.danger : "var(--pc-ui-00d4aa, #00d4aa)" }}>R$ {fmt(Math.abs(lucroEstoque))}</div>
                </div>
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 11, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, color: C.muted }}>Faturamento potencial</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: C.text }}>R$ {fmt(faturamentoEstoque)}</div>
                </div>
                <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 2 }}>
                  {pecasEmEstoque} peça{pecasEmEstoque === 1 ? "" : "s"} em estoque, em {produtosEmEstoque.length} produto{produtosEmEstoque.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const btnSmall = {
  width: 24, height: 24, borderRadius: 6,
  background: "rgba(0,212,170,0.1)",
  border: "1px solid rgba(0,212,170,0.25)",
  color: "var(--pc-ui-00d4aa, #00d4aa)", fontSize: 14, fontWeight: 900,
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1, padding: 0,
};

const tipoCorMap = {
  VIP: "var(--pc-ui-f59e0b, #f59e0b)", Frequente: "var(--pc-ui-00d4aa, #00d4aa)", Novo: "var(--pc-ui-38bdf8, #38bdf8)", Problemático: "#ef4444",
};
